'use client'

import { useEffect } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { SessionCache } from '@/lib/session-cache'
import type { UserRole } from '@planner/types'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, userRole } = useAuth()
  const router = useRouter()
  const pathname = usePathname()

  // Public routes that don't require authentication
  const publicRoutes = ['/', '/login', '/register', '/register/confirm-email', '/verify-email']
  
  // Calculate isPublicRoute - simple and synchronous
  const normalizedPathname = pathname
    ? (pathname === '/' ? '/' : pathname.replace(/\/$/, ''))
    : '/'
  
  // Check if pathname matches public routes or is an invitation acceptance page
  const isPublicRoute = publicRoutes.includes(normalizedPathname) || 
    normalizedPathname === '' ||
    (normalizedPathname.startsWith('/team/invite/') && normalizedPathname !== '/team/invite')
  
  const renderStartTime = Date.now()
  console.log(`[ProtectedRoute] RENDER: pathname=${pathname}, user=${!!user}, userRole=${userRole}, isPublicRoute=${isPublicRoute}`)

  useEffect(() => {
    // Allow public routes
    if (isPublicRoute) {
      console.log(`[ProtectedRoute] Public route, no auth check, pathname=${pathname}`)
      return
    }

    // Check if we have authentication (user OR cache)
    const hasCache = SessionCache.hasCache()
    const hasAuth = user || hasCache
    console.log(`[ProtectedRoute] Auth check: user=${!!user}, hasCache=${hasCache}, hasAuth=${hasAuth}, pathname=${pathname}`)

    // If we have auth, check role-based access
    if (hasAuth) {
      if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
        console.log(`[ProtectedRoute] Role check failed, redirecting to dashboard, pathname=${pathname}`)
        router.push('/dashboard')
      }
      return
    }

    // No auth - redirect to login after short delay (to allow cache to load)
    console.log(`[ProtectedRoute] No auth, scheduling redirect, pathname=${pathname}`)
    const redirectTimer = setTimeout(() => {
      // Triple-check before redirecting (cache might have loaded)
      const cacheExists = SessionCache.hasCache()
      if (!cacheExists && !user) {
        console.log(`[ProtectedRoute] Redirecting to login, pathname=${pathname}`)
        router.push('/login')
      } else {
        console.log(`[ProtectedRoute] Redirect cancelled - cache/user found, pathname=${pathname}`)
      }
    }, 200) // Short delay to allow cache to load
    
    return () => clearTimeout(redirectTimer)
  }, [user, userRole, isPublicRoute, router, allowedRoles, pathname])

  // ALWAYS RENDER CHILDREN - NO LOADING FALLBACK
  // This prevents "Ładowanie..." after tab switch
  // If user is not authenticated, useEffect will redirect to /login in the background
  const renderDuration = Date.now() - renderStartTime
  void renderDuration
  console.log(`[ProtectedRoute] RENDERING children, pathname=${pathname}`)
  return <>{children}</>
}
